import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProductListLogicReturn } from './ProductList.types';
import { fetchProducts, addToCart, fetchCartItems, selectUser, getUserById, selectSelectedCategoryId } from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * 상품 데이터 정규화 함수
 * API 필드명과 스킨에서 사용하는 필드명 매핑
 */
const normalizeProduct = (product: any, userInfo: any, levelPolicies: any[] = []) => {
  const basePrice = product.newPrice || product.price;
  let finalPrice = basePrice;
  let levelPrice = null;
  let levelName = null;
  let hasLevelPrice = false;
  
  // 기본 PV 값
  let pv = product.config?.generate_pv || 0;

  // optionJson.priority가 있는 경우에만 등급/직급별 가격 및 PV 적용
  if (product.optionJson && userInfo) {
    // 사용자가 가진 level에 따라 적용 (priority와 무관하게 실제 데이터 기준)
    
    // level1 체크 (사용자가 level1을 가지고 있고, 상품에 level1 데이터가 있는 경우)
    if (userInfo.level1 && (product.optionJson.level1_price || product.optionJson.level1_pv)) {
      // 등급별 가격
      const foundPrice = product.optionJson.level1_price?.[userInfo.level1];
      if (foundPrice !== undefined && foundPrice !== null) {
        levelPrice = Number(foundPrice);
        // levelPolicies에서 level1 정책 찾기
        const level1Policy = Array.isArray(levelPolicies) ? levelPolicies.find((p: any) => p.id === userInfo.level1) : undefined;
        const level1Name = level1Policy?.name || level1Policy?.customName || level1Policy?.type || '';
        levelName = level1Name ? `${level1Name} 할인` : '등급 할인';
        hasLevelPrice = true;
        finalPrice = levelPrice;
      }
      
      // 등급별 PV
      const foundPV = product.optionJson.level1_pv?.[userInfo.level1];
      if (foundPV !== undefined && foundPV !== null) {
        pv = Number(foundPV);
      }
    }
    // level2 체크 (사용자가 level2를 가지고 있고, 상품에 level2 데이터가 있는 경우)
    else if (userInfo.level2 && (product.optionJson.level2_price || product.optionJson.level2_pv)) {
      // 직급별 가격
      const foundPrice = product.optionJson.level2_price?.[userInfo.level2];
      if (foundPrice !== undefined && foundPrice !== null) {
        levelPrice = Number(foundPrice);
        // levelPolicies에서 level2 정책 찾기
        const level2Policy = Array.isArray(levelPolicies) ? levelPolicies.find((p: any) => p.id === userInfo.level2) : undefined;
        console.log('[normalizeProduct] Found level2Policy:', level2Policy);
        const level2Name = level2Policy?.name || level2Policy?.customName || level2Policy?.type || '';
        levelName = level2Name ? `${level2Name} 할인` : '직급 할인';
        console.log('[normalizeProduct] level2Name:', level2Name, 'levelName:', levelName);
        hasLevelPrice = true;
        finalPrice = levelPrice;
      }
      
      // 직급별 PV
      const foundPV = product.optionJson.level2_pv?.[userInfo.level2];
      if (foundPV !== undefined && foundPV !== null) {
        pv = Number(foundPV);
      }
    }
  }
  
  return {
    ...product,
    // 필드명 매핑 (하위 호환성 유지)
    name: product.title || product.name,
    price: finalPrice,
    originalPrice: basePrice, // 원래 가격
    image: product.thumbnail || product.image,
    // PV 값 - 등급/직급별 PV 또는 기본 PV
    pv: pv,
    // 등급/직급 정보 추가
    levelPrice,
    levelName,
    hasLevelPrice
  };
};

/**
 * 상품 목록 컴포넌트 로직 훅
 */
const useProductListLogic = (componentData: any, mode: string): ProductListLogicReturn => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const user = useSelector((state: any) => state.withcookie?.user);
  const userInfo = useSelector(selectUser);
  const userInfoFromState = useSelector((state: any) => state.withcookie?.userInfo);
  const apiProducts = useSelector((state: any) => state.withcookie?.products || []);
  const apiTotalCount = useSelector((state: any) => state.withcookie?.productsTotalCount || 0);
  const selectedCategoryId = useSelector(selectSelectedCategoryId);
  const withcookieData = useSelector((state: any) => state.withcookie?.withcookieData);
  const levelPolicies = useMemo(() => {
    return withcookieData?.skin?.company?.levelpolicies || 
           withcookieData?.company?.levelpolicies || 
           [];
  }, [withcookieData]);
  
  // URL 파라미터에서 t 값 추출
  const searchParams = new URLSearchParams(location.search);
  const titleParam = searchParams.get('t');
  

  // 호환성을 위한 props 변환
  const props = { ...componentData.props, ...componentData.componentProps, ...componentData, mode };
  
  // 상태 관리
  const [products, setProducts] = useState<any[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // 모바일 관련 상태
  const [isMobile, setIsMobile] = useState(false);
  const [mobileProducts, setMobileProducts] = useState<any[]>([]);
  const [mobilePage, setMobilePage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // 무한 스크롤을 위한 ref
  const loadMoreButtonRef = useRef<HTMLButtonElement | null>(null);
  const autoLoadTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [sortBy, setSortBy] = useState(props.componentProps?.sortBy || 'created');
  const [sortOrder, setSortOrder] = useState(props.componentProps?.sortOrder || 'desc');
  
  // 옵션 추출 - props, componentProps, defaultProps 순서로 확인
  const {
    itemsPerRow = 4,
    itemsPerPage = 20,  // 기본값을 8로 변경
    categoryId = null,
    include_product_ids = [],
    exclude_product_ids = [],
    include_category_ids = [],
    exclude_category_ids = [],
    showStock = true
  } = { ...props, ...(props.componentProps || {}) };
  

  // 관리자 모드 확인
  const isAdminMode = mode === 'editor' || mode === 'preview';
  
  // 사용자 상세 정보 로드 중복 방지를 위한 ref
  const userDetailFetchedRef = useRef(false);
  
  // 초기 로드 여부 추적
  const isInitialMount = useRef(true);
  
  // 사용자 상세 정보 로드
  useEffect(() => {
    // 사용자가 있고, 아직 상세 정보를 가져오지 않았을 때만 실행
    if (user && user.id && !userDetailFetchedRef.current && !userInfo && !userInfoFromState) {
      userDetailFetchedRef.current = true;
      dispatch(getUserById({ userId: user.id }) as any);
    }
  }, [user?.id, dispatch, userInfo, userInfoFromState]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 사용자 변경 시 ref 초기화
  useEffect(() => {
    if (user?.id) {
      userDetailFetchedRef.current = false;
    }
  }, [user?.id]);
  
  // 모바일 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // 초기 마운트 시에만 상품 데이터 가져오기 (PC용)
  useEffect(() => {
    if (!isMobile && isInitialMount.current) {
      // 초기 마운트 시에만 자체 API 호출
      const params: any = { page: currentPage, per_page: itemsPerPage };
      // URL 파라미터 t가 있으면 title로 추가
      if (titleParam) {
        params.title = titleParam;
      }
      dispatch(fetchProducts(params) as any);
      isInitialMount.current = false;
    }
  }, [dispatch, itemsPerPage, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 페이지 변경 시 상품 데이터 가져오기 (PC용)
  // selectedCategoryId를 의존성에서 제거하여 카테고리 변경 시 중복 호출 방지
  useEffect(() => {
    if (!isMobile && !isInitialMount.current) {
      // 기본 파라미터 설정
      const params: any = { page: currentPage, per_page: itemsPerPage };
      
      // URL 파라미터 t가 있으면 title로 추가
      if (titleParam) {
        params.title = titleParam;
      }
      
      // 페이지 변경 시에만 API 호출 (CategoryMenu에서 선택한 카테고리가 있어도 페이지네이션은 작동해야 함)
      if (selectedCategoryId !== undefined && selectedCategoryId !== null) {
        // CategoryMenu에서 선택한 카테고리로 페이지네이션
        params.include_category_ids = [selectedCategoryId];
        dispatch(fetchProducts(params) as any);
      } else if (selectedCategoryId === null) {
        // 전체 카테고리로 페이지네이션
        params.include_category_ids = [];
        dispatch(fetchProducts(params) as any);
      } else {
        // CategoryMenu가 없는 경우 일반 페이지네이션
        dispatch(fetchProducts(params) as any);
      }
    }
  }, [dispatch, currentPage, itemsPerPage, isMobile]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 카테고리 변경 시 currentPage를 1로 리셋
  // CategoryMenu에서 이미 fetchProducts를 호출하므로 여기서는 페이지만 리셋
  useEffect(() => {
    if (!isInitialMount.current && selectedCategoryId !== undefined) {
      setCurrentPage(1);
    }
  }, [selectedCategoryId]);
  
  // 모바일용 초기 데이터 가져오기
  useEffect(() => {
    if (isMobile) {
      setMobilePage(1);
      setMobileProducts([]);
      // 모바일 전환 시 첫 페이지 데이터 가져오기
      const fetchParams: any = { page: 1, per_page: itemsPerPage };
      
      // URL 파라미터 t가 있으면 title로 추가
      if (titleParam) {
        fetchParams.title = titleParam;
      }
      
      // CategoryMenu에서 선택한 카테고리가 있으면 포함
      if (selectedCategoryId !== undefined && selectedCategoryId !== null) {
        fetchParams.include_category_ids = [selectedCategoryId];
      }
      
      dispatch(fetchProducts(fetchParams) as any).then((response: any) => {
        if (response.payload && response.payload.products) {
          let filteredData = response.payload.products.map((product: any) => normalizeProduct(product, userInfo, levelPolicies));
          
          // 필터링 적용
          // selectedCategoryId가 있으면 우선 사용 (CategoryMenu에서 선택한 카테고리)
          if (selectedCategoryId !== undefined && selectedCategoryId !== null) {
            // selectedCategoryId가 있으면 서버에서 이미 필터링되어 옴
          } else if (categoryId || selectedCategory) {
            const targetCategory = selectedCategory || categoryId;
            filteredData = filteredData.filter((product: any) => 
              product.category_id === targetCategory
            );
          }
          
          if (include_product_ids && include_product_ids.length > 0) {
            filteredData = filteredData.filter((product: any) => 
              include_product_ids.includes(product.id)
            );
          }
          
          if (exclude_product_ids && exclude_product_ids.length > 0) {
            filteredData = filteredData.filter((product: any) => 
              !exclude_product_ids.includes(product.id)
            );
          }
          
          if (include_category_ids && include_category_ids.length > 0) {
            filteredData = filteredData.filter((product: any) => 
              include_category_ids.includes(product.category_id)
            );
          }
          
          if (exclude_category_ids && exclude_category_ids.length > 0) {
            filteredData = filteredData.filter((product: any) => 
              !exclude_category_ids.includes(product.category_id)
            );
          }
          
          setMobileProducts(filteredData);
        }
      });
    }
  }, [dispatch, isMobile, itemsPerPage, categoryId, selectedCategory, include_product_ids, exclude_product_ids, include_category_ids, exclude_category_ids, selectedCategoryId, userInfo]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 상품 데이터 초기화 (PC용)
  useEffect(() => {
    if (!isMobile && apiProducts && apiProducts.length > 0) {
      // 사용자 정보 우선순위: userInfoFromState > userInfo > user
      const finalUserInfo = userInfoFromState || userInfo || user;
      
      // 디버깅용 로그 - 전체 객체 구조 확인
      console.log('[ProductListLogic] Full UserInfo Structure:');
      console.log('userInfo:', userInfo);
      console.log('userInfoFromState:', userInfoFromState);
      console.log('finalUserInfo:', finalUserInfo);
      
      // level2가 155/156인데 어떤 정책 정보가 있는지 확인
      if (finalUserInfo?.level2) {
        console.log('Level2 ID:', finalUserInfo.level2);
        console.log('Available levelPolicies:', levelPolicies);
        // levelPolicies에서 현재 level2에 해당하는 정책 찾기
        const currentLevel2Policy = Array.isArray(levelPolicies) ? levelPolicies.find((p: any) => p.id === finalUserInfo.level2) : undefined;
        console.log('Current Level2 Policy:', currentLevel2Policy);
      }

      // 상품 데이터 정규화
      let filteredData = apiProducts.map((product: any) => normalizeProduct(product, finalUserInfo, levelPolicies));
      
      // 카테고리 필터링
      if (categoryId || selectedCategory) {
        const targetCategory = selectedCategory || categoryId;
        filteredData = filteredData.filter((product: any) => 
          product.category_id === targetCategory
        );
      }
      
      // 포함/제외 필터링
      if (include_product_ids && include_product_ids.length > 0) {
        filteredData = filteredData.filter((product: any) => 
          include_product_ids.includes(product.id)
        );
      }
      
      if (exclude_product_ids && exclude_product_ids.length > 0) {
        filteredData = filteredData.filter((product: any) => 
          !exclude_product_ids.includes(product.id)
        );
      }
      
      if (include_category_ids && include_category_ids.length > 0) {
        filteredData = filteredData.filter((product: any) => 
          include_category_ids.includes(product.category_id)
        );
      }
      
      if (exclude_category_ids && exclude_category_ids.length > 0) {
        filteredData = filteredData.filter((product: any) => 
          !exclude_category_ids.includes(product.category_id)
        );
      }
      
      setProducts(filteredData);
    } else if (!isMobile) {
      setProducts([]);
    }
  }, [apiProducts, categoryId, selectedCategory, include_product_ids, exclude_product_ids, include_category_ids, exclude_category_ids, isMobile, userInfo, userInfoFromState, user, levelPolicies]); // eslint-disable-line react-hooks/exhaustive-deps
  
  
  // 검색 및 정렬
  useEffect(() => {


    // products가 있을 때만 필터링
    if (products.length > 0) {
      let result = [...products];
      
      // 검색 (title과 name 모두 지원)
      if (searchQuery) {
        result = result.filter(product => 
          (product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           product.title?.toLowerCase().includes(searchQuery.toLowerCase())) ||
          product.description?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }
      
      // 정렬
      result.sort((a, b) => {
        let compareValue = 0;
        
        switch (sortBy) {
          case 'name':
            // name과 title 모두 지원
            const aName = a.name || a.title || '';
            const bName = b.name || b.title || '';
            compareValue = aName.localeCompare(bName);
            break;
          case 'price':
            // price와 newPrice 모두 지원
            const aPrice = a.price || a.newPrice || 0;
            const bPrice = b.price || b.newPrice || 0;
            compareValue = aPrice - bPrice;
            break;
          case 'created':
            compareValue = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            break;
          case 'stock':
            compareValue = (a.stock || 0) - (b.stock || 0);
            break;
          default:
            compareValue = 0;
        }
        
        return sortOrder === 'asc' ? compareValue : -compareValue;
      });
      

      setFilteredProducts(result);
    } else {
      // products가 없으면 빈 배열로 설정
      setFilteredProducts([]);
    }
  }, [products, searchQuery, sortBy, sortOrder]);
  
  // filteredProducts가 초기화되지 않았거나 비어있고 products가 있으면 products 사용
  const displayProducts = (filteredProducts.length === 0 && products.length > 0) ? products : filteredProducts;
  
  // 페이지네이션 계산
  const finalProducts = isMobile ? mobileProducts : (products.length > 0 ? products : filteredProducts);
  // API의 totalCount를 사용하여 totalPages 계산
  const totalPages = apiTotalCount > 0 ? Math.ceil(apiTotalCount / itemsPerPage) : Math.ceil(finalProducts.length / itemsPerPage);
  // 서버 사이드 페이지네이션 - API에서 이미 페이지별 데이터를 받으므로 슬라이싱 불필요
  const currentProducts = isMobile ? mobileProducts : finalProducts;
  


  // 장바구니 추가
  const handleAddToCart = useCallback(async (product: any) => {

    // 로그인 체크
    if (!user) {
      alert(t('로그인이 필요합니다.'));
      navigate('/login');
      return;
    }

    // hasOptions가 true인 경우 상세페이지로 이동
    if (product.hasOptions) {
      navigate(`/products/${product.id}`);
      return;
    }
    
    try {
      // setLoading 제거 - 전체 상품 목록이 사라지는 문제 방지
      
      // Redux action dispatch - 올바른 액션 사용
      await dispatch(addToCart({
        product_id: product.id,
        variant_id: product.variant_id || null,
        count: 1
      }) as any);
      
      // 원본 코드처럼 alert 사용
      alert(t('상품') + ` "${product.name || product.title}"` + t('이(가) 장바구니에 추가되었습니다.'));
      
      // alert 후에 장바구니 아이템 새로고침 (비동기로 처리)
      dispatch(fetchCartItems() as any);
    } catch (error) {
      console.error('장바구니 추가 실패:', error);
      alert(t('장바구니 추가에 실패했습니다. 다시 시도해주세요.'));
    }
  }, [dispatch, t, navigate, user]);
  
  // 카테고리 변경
  const handleCategoryChange = useCallback((categoryId: string | null) => {
    setSelectedCategory(categoryId);
    setCurrentPage(1);
  }, []);
  
  // 검색
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);  // 검색 시 첫 페이지로 이동
  }, []);
  
  // 정렬 변경
  const handleSortChange = useCallback((newSortBy: string, newSortOrder: string) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  }, []);
  
  // 페이지 변경
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // 모바일이 아닐 때만 스크롤 최상단으로 (PC 페이지네이션)
    if (!isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isMobile]);
  
  // 상품 클릭 핸들러
  const handleProductClick = useCallback((product: any) => {
    // 모바일 상태 저장
    if (isMobile && mobileProducts.length > 0) {
      // 모바일 상태를 세션 스토리지에 저장
      const mobileState = {
        mobileProducts,
        mobilePage,
        scrollPosition: window.pageYOffset,
        totalPages
      };
      sessionStorage.setItem('productListMobileState', JSON.stringify(mobileState));
      sessionStorage.setItem('withcookie_from_back', 'true');
    }

    // 네비게이션 처리
    if (componentData.utils?.navigate) {
      componentData.utils.navigate(`/products/${product.id}`);
    } else if (props.utils?.navigate) {
      props.utils.navigate(`/products/${product.id}`);
    } else {
      // 에디터 모드 체크
      if (window.location.pathname.includes('/edit/')) {
        // 에디터 모드에서는 네비게이션 하지 않음
      } else {
        // fallback: window.location.href 사용
        window.location.href = `/products/${product.id}`;
      }
    }
  }, [isMobile, mobileProducts, mobilePage, totalPages, componentData.utils, props.utils]);

  // 모바일 더보기 핸들러
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || mobilePage >= totalPages) return;
    
    setIsLoadingMore(true);
    const nextPage = mobilePage + 1;
    
    try {
      const fetchParams: any = { page: nextPage, per_page: itemsPerPage };
      
      // URL 파라미터 t가 있으면 title로 추가
      if (titleParam) {
        fetchParams.title = titleParam;
      }
      
      // CategoryMenu에서 선택한 카테고리가 있으면 포함
      if (selectedCategoryId !== undefined && selectedCategoryId !== null) {
        fetchParams.include_category_ids = [selectedCategoryId];
      }
      
      const response = await dispatch(fetchProducts(fetchParams) as any);
      
      // 응답 데이터 처리
      if (response.payload && response.payload.products) {
        let newProducts = response.payload.products.map((product: any) => normalizeProduct(product, userInfo, levelPolicies));
        
        // 필터링 적용
        // selectedCategoryId가 있으면 우선 사용 (CategoryMenu에서 선택한 카테고리)
        if (selectedCategoryId !== undefined && selectedCategoryId !== null) {
          // selectedCategoryId가 있으면 서버에서 이미 필터링되어 옴
        } else if (categoryId || selectedCategory) {
          const targetCategory = selectedCategory || categoryId;
          newProducts = newProducts.filter((product: any) => 
            product.category_id === targetCategory
          );
        }
        
        if (include_product_ids && include_product_ids.length > 0) {
          newProducts = newProducts.filter((product: any) => 
            include_product_ids.includes(product.id)
          );
        }
        
        if (exclude_product_ids && exclude_product_ids.length > 0) {
          newProducts = newProducts.filter((product: any) => 
            !exclude_product_ids.includes(product.id)
          );
        }
        
        if (include_category_ids && include_category_ids.length > 0) {
          newProducts = newProducts.filter((product: any) => 
            include_category_ids.includes(product.category_id)
          );
        }
        
        if (exclude_category_ids && exclude_category_ids.length > 0) {
          newProducts = newProducts.filter((product: any) => 
            !exclude_category_ids.includes(product.category_id)
          );
        }
        
        // 기존 상품에 새 상품 추가 (중복 ID 체크)
        setMobileProducts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const uniqueNewProducts = newProducts.filter((product: any) => !existingIds.has(product.id));
          const updated = [...prev, ...uniqueNewProducts];
          
          return updated;
        });
      }
      
      setMobilePage(nextPage);
    } catch (error) {
      console.error('더보기 로드 실패:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [dispatch, isLoadingMore, mobilePage, totalPages, itemsPerPage, categoryId, selectedCategory, include_product_ids, exclude_product_ids, include_category_ids, exclude_category_ids, userInfo, titleParam, selectedCategoryId, levelPolicies]);
  
  // 더보기 버튼 자동 클릭을 위한 Intersection Observer 설정
  useEffect(() => {
    if (!isMobile || !loadMoreButtonRef.current) return;
    
    const currentButtonRef = loadMoreButtonRef.current;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 이미 로딩 중이면 중복 실행 방지
            if (isLoadingMore || mobilePage >= totalPages) return;
            
            // 기존 타이머 취소
            if (autoLoadTimerRef.current) {
              clearTimeout(autoLoadTimerRef.current);
            }
            
            // 0.5초 후 자동 클릭
            autoLoadTimerRef.current = setTimeout(() => {
              if (!isLoadingMore && mobilePage < totalPages && loadMoreButtonRef.current) {
                handleLoadMore();
              }
            }, 500);
          } else {
            // 버튼이 화면에서 사라졌을 때 타이머 취소
            if (autoLoadTimerRef.current) {
              clearTimeout(autoLoadTimerRef.current);
              autoLoadTimerRef.current = null;
            }
          }
        });
      },
      {
        threshold: 0.5 // 버튼의 50% 이상이 보일 때 감지
      }
    );
    
    observer.observe(currentButtonRef);
    
    // 클린업
    return () => {
      observer.disconnect();
      if (autoLoadTimerRef.current) {
        clearTimeout(autoLoadTimerRef.current);
      }
    };
  }, [isMobile, isLoadingMore, mobilePage, totalPages, handleLoadMore]);
  
  // 디버깅용 로그

  const returnValue = {
    data: {
      products: currentProducts,
      loading: false, // 페이지 변경 시 로딩 표시 제거
      currentPage,
      totalPages,
      totalProducts: apiTotalCount > 0 ? apiTotalCount : displayProducts.length,
      selectedCategory,
      searchQuery,
      sortBy,
      sortOrder,
      isUserLoggedIn: !!user,
      isAdminMode,
      itemsPerRow,
      showStock,
      theme: props.theme || {},
      // 모바일 관련 데이터
      isMobile,
      mobileProducts,
      mobilePage,
      isLoadingMore,
      loadMoreButtonRef
    },
    actions: {
      handleAddToCart,
      handleCategoryChange,
      handleSearch,
      handleSortChange,
      handlePageChange,
      handleLoadMore,
      handleProductClick
    }
  };



  return returnValue;
};

export default useProductListLogic;